<template>
  <v-container fluid class="pa-4">
    <!-- Header Section -->
    <v-row class="mb-6">
      <v-col cols="12">
        <div class="d-flex align-center mb-2">
          <v-avatar size="56" class="mr-4 primary">
            <v-icon size="32" color="white">mdi-account-check</v-icon>
          </v-avatar>
          <div>
            <h1 class="display-1 mb-1 font-weight-bold">Member Enrollment</h1>
            <p class="subtitle-1 grey--text text--darken-1 mb-0">
              Manage your enrollment benefits and specialist selections
            </p>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Loading State -->
    <v-card v-if="loading" class="mb-4" elevation="2">
      <v-card-text class="pa-6">
        <div class="text-center">
          <v-progress-circular indeterminate color="primary" size="64" width="6" class="mb-4"></v-progress-circular>
          <div class="title">Loading members...</div>
          <div class="caption grey--text mt-2">Please wait while we fetch your enrollment information</div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Error State -->
    <v-card v-else-if="error" class="mb-4" elevation="2" outlined>
      <v-alert type="error" prominent border="left" colored-border>
        <v-alert-title class="headline">Error</v-alert-title>
        <div class="mt-2">{{ error }}</div>
      </v-alert>
    </v-card>

    <!-- Empty State -->
    <v-card v-else-if="members.length === 0" class="mb-4" elevation="2" outlined>
      <v-card-text class="pa-6 text-center">
        <v-icon size="80" color="grey lighten-1" class="mb-4">mdi-account-off</v-icon>
        <h2 class="headline mb-2">No Members Found</h2>
        <p class="body-1 grey--text">
          No members found for your account. Please contact support if you believe this is an error.
        </p>
      </v-card-text>
    </v-card>

    <!-- Members List -->
    <div v-else>
      <v-expansion-panels
        v-model="expandedPanels"
        :multiple="false"
        class="member-enrollment-panels"
        :disabled="loading"
      >
        <v-expansion-panel
          v-for="(family, index) in members"
          :key="family.subscriberId"
          :value="index"
          class="mb-4"
        >
          <v-expansion-panel-header class="expansion-header">
            <template v-slot:default="{ open }">
              <div class="d-flex align-center" style="width: 100%">
                <v-avatar size="48" :color="open ? 'primary' : 'grey lighten-2'" class="mr-4">
                  <v-icon :color="open ? 'white' : 'grey darken-1'" size="28">mdi-account-group</v-icon>
                </v-avatar>
                <div class="flex-grow-1">
                  <div class="font-weight-bold text-h6 mb-1">
                    {{ family.subscriber.FIRST_NAME }} {{ family.subscriber.LAST_NAME }}
                  </div>
                  <div class="d-flex align-center flex-wrap">
                    <v-chip small outlined color="primary" class="mr-2 mb-1">
                      <v-icon small left>mdi-identifier</v-icon>
                      {{ family.subscriberId }}
                    </v-chip>
                    <span v-if="family.dependents.length > 0" class="caption grey--text">
                      <v-icon small class="mr-1">mdi-account-child</v-icon>
                      {{ family.dependents.length }} dependent{{ family.dependents.length > 1 ? 's' : '' }}
                    </span>
                  </div>
                </div>
                <v-chip v-if="open" small color="primary" text-color="white" class="ml-auto">
                  <v-icon small left>mdi-chevron-up</v-icon>
                  Expanded
                </v-chip>
              </div>
            </template>
          </v-expansion-panel-header>

          <v-expansion-panel-content>
            <div class="pa-4">
              <!-- Subscriber Enrollment Card -->
              <v-expansion-panels v-model="memberPanels[family.subscriberId]" multiple flat class="member-detail-panels">
                <v-expansion-panel class="mb-4 subscriber-card" elevation="3">
                  <v-expansion-panel-header class="primary white--text member-detail-header">
                    <div class="d-flex align-center">
                      <v-icon class="mr-2" color="white">mdi-account-star</v-icon>
                      <div class="flex-grow-1">
                        <div class="text-h6 font-weight-bold">
                          {{ family.subscriber.FIRST_NAME }} {{ family.subscriber.LAST_NAME }}
                        </div>
                        <div class="caption text--lighten-2 mt-1">Subscriber</div>
                      </div>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content class="pa-0">
                    <div class="pa-4">
                  <member-enrollment-details
                    ref="enrollmentDetails"
                    :key="`subscriber-${family.subscriber.MEMBER_ID}`"
                    :enrollment-refresh-key="reloadKey"
                    :member="simplifyMember(family.subscriber)"
                    :insurance="insurance"
                    :pcp-modal-open="showPCPModal && selectedMember && (selectedMember.MEMBER_ID === (family.subscriber.MEMBER_ID || family.subscriber.SUBSCRIBER_ID))"
                    :specialist-modal-open="showSpecialistModal && selectedMember && (selectedMember.MEMBER_ID === (family.subscriber.MEMBER_ID || family.subscriber.SUBSCRIBER_ID))"
                    @select-specialist="openSpecialistModal"
                    @change-specialist="openSpecialistModal"
                    @select-pcp="openPCPModal"
                    @change-pcp="openPCPModal"
                  />
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>

              <!-- Dependents Enrollment Cards -->
                <v-expansion-panel
                  v-for="dependent in family.dependents"
                  :key="dependent.MEMBER_ID"
                  class="mb-4 dependent-card"
                  elevation="3"
                >
                  <v-expansion-panel-header class="primary white--text member-detail-header">
                    <div class="d-flex align-center">
                      <v-icon class="mr-2" color="white">mdi-account-child</v-icon>
                      <div class="flex-grow-1">
                        <div class="text-h6 font-weight-bold">
                          {{ dependent.FIRST_NAME }} {{ dependent.LAST_NAME }}
                        </div>
                        <div class="d-flex align-center mt-1">
                          <span class="caption text--lighten-2 mr-3">Dependent</span>
                          <v-chip v-if="dependent.RELATIONSHIP" x-small outlined color="white" class="text--lighten-3">
                            {{ dependent.RELATIONSHIP }}
                          </v-chip>
                        </div>
                      </div>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content class="pa-0">
                    <div class="pa-4">
                      <member-enrollment-details
                        ref="enrollmentDetails"
                        :key="`dependent-${dependent.MEMBER_ID}`"
                        :enrollment-refresh-key="reloadKey"
                        :member="simplifyMember(dependent)"
                        :insurance="insurance"
                        :pcp-modal-open="showPCPModal && selectedMember && (selectedMember.MEMBER_ID === (dependent.MEMBER_ID || dependent.SUBSCRIBER_ID))"
                        :specialist-modal-open="showSpecialistModal && selectedMember && (selectedMember.MEMBER_ID === (dependent.MEMBER_ID || dependent.SUBSCRIBER_ID))"
                        @select-specialist="openSpecialistModal"
                        @change-specialist="openSpecialistModal"
                        @select-pcp="openPCPModal"
                        @change-pcp="openPCPModal"
                      />
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>

    <!-- Specialist Selection Modal -->
    <specialist-selection-modal
      v-if="showSpecialistModal"
      :key="`specialist-modal-${specialistModalKey}`"
      :show="showSpecialistModal"
      :specialty="selectedSpecialty"
      :member="selectedMember"
      :insurance="insurance"
      @close="closeSpecialistModal"
      @selected="onSpecialistSelected"
    />

    <!-- PCP Selection Modal -->
    <PCPSectionModal
      v-if="showPCPModal"
      :key="`pcp-modal-${pcpModalKey}`"
      :show="showPCPModal"
      :member="selectedMember"
      :insurance="insurance"
      @close="closePCPModal"
      @selected="onPCPSelected"
    />

    <!-- Confirm PCP/specialist change effective next month -->
    <v-dialog v-model="providerChangeDialog.open" max-width="520" persistent>
      <v-card>
        <v-card-title class="headline">Confirm provider change</v-card-title>
        <v-card-text class="pt-2">
          <p class="body-1">{{ providerChangeDialog.message }}</p>
          <p v-if="providerChangeDialog.effectiveDateDisplay" class="subtitle-1 font-weight-medium primary--text">
            Effective date: {{ providerChangeDialog.effectiveDateDisplay }}
          </p>
          <v-alert v-if="providerChangeDialog.customerServiceNotice" type="info" dense outlined class="mt-3 mb-0">
            {{ providerChangeDialog.customerServiceNotice }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelProviderChangeDialog">Cancel</v-btn>
          <v-btn color="primary" @click="confirmProviderChangeDialog">Confirm change</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="snackbar.timeout" top>
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from 'axios';
import MemberEnrollmentDetails from '../components/enrollment/MemberEnrollmentDetails.vue';
import SpecialistSelectionModal from '../components/enrollment/SpecialistSelectionModal.vue';
import PCPSectionModal from '../components/enrollment/PCPSectionModal.vue';

export default {
  name: 'MemberEnrollment',
  components: {
    MemberEnrollmentDetails,
    SpecialistSelectionModal,
    PCPSectionModal,
  },
  data() {
    return {
      members: [],
      loading: true,
      error: null,
      insurance: '',
      expandedPanels: null,
      memberPanels: {},
      showSpecialistModal: false,
      showPCPModal: false,
      selectedSpecialty: null,
      selectedMember: null,
      reloadKey: 0,
      pcpModalKey: 0,
      specialistModalKey: 0,
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 3000,
      },
      providerChangeDialog: {
        open: false,
        message: '',
        effectiveDateDisplay: '',
        customerServiceNotice: '',
      },
    };
  },
  created() {
    this._providerChangeResolve = null;
  },
  async mounted() {
    await this.getUser();
    if (this.insurance) {
      await this.loadMembers();
    } else {
      this.loading = false;
      this.error = 'No insurance information found. Please contact support.';
    }
  },
  methods: {
    async getUser() {
      try {
        const response = await axios.get('/api/user');
        if (response.data && response.data.length > 0) {
          const user = response.data[0];
          const ins = user.insurance;
          this.insurance = Array.isArray(ins) ? ins[0] : (ins || '');
        }
      } catch {
        this.error = 'Error loading user data. Please try again.';
        this.loading = false;
      }
    },
    async loadMembers() {
      this.loading = true;
      this.error = null;
      try {
        const response = await axios.get(`/api/enrollment/members/${this.insurance}`);
        const rawMembers = response.data || [];
        this.members = rawMembers.map((family) => ({
          subscriber: this.simplifyMember(family.subscriber || {}),
          dependents: Array.isArray(family.dependents)
            ? family.dependents.map((dep) => this.simplifyMember(dep)).filter(Boolean)
            : [],
          subscriberId: family.subscriberId || (family.subscriber && family.subscriber.MEMBER_ID) || null,
        }));
        this.members.forEach((family) => {
          this.$set(
            this.memberPanels,
            family.subscriberId,
            Array.from({ length: 1 + family.dependents.length }, (_, index) => index)
          );
        });
        if (this.members.length > 0) {
          this.expandedPanels = 0;
        }
      } catch (error) {
        const errData = error.response && error.response.data;
        const msg = (errData && errData.error) || (error.response && error.response.statusText) || 'Unknown error';
        this.error = 'Error loading members: ' + msg;
      } finally {
        this.loading = false;
      }
    },
    simplifyMember(member) {
      if (!member) return null;
      return {
        MEMBER_ID: member.MEMBER_ID || member.memberId || member.member_id || null,
        SUBSCRIBER_ID: member.SUBSCRIBER_ID || member.subscriberId || member.subscriber_id || null,
        FIRST_NAME: member.FIRST_NAME || member.firstName || member.first_name || null,
        LAST_NAME: member.LAST_NAME || member.lastName || member.last_name || null,
        DOB: member.DOB || member.dob || member.dateOfBirth || null,
        GENDER: member.GENDER || member.gender || null,
        RELATIONSHIP: member.RELATIONSHIP || member.RELATIONSHIP_TO_SUBSCRIBER || member.relationship || null,
      };
    },
    openSpecialistModal(member, specialty) {
      this.specialistModalKey++;
      this.selectedMember = member;
      this.selectedSpecialty = specialty;
      this.$nextTick(() => {
        this.showSpecialistModal = true;
      });
    },
    closeSpecialistModal() {
      this.showSpecialistModal = false;
      this.selectedMember = null;
      this.selectedSpecialty = null;
    },
    openPCPModal(member) {
      if (!member || !member.MEMBER_ID) {
        this.showSnackbar('Error: Member information is missing', 'error');
        return;
      }
      this.pcpModalKey++;
      this.selectedMember = { MEMBER_ID: member.MEMBER_ID };
      this.$nextTick(() => {
        this.showPCPModal = true;
      });
    },
    closePCPModal() {
      this.showPCPModal = false;
      this.selectedMember = null;
    },
    openProviderChangeDialog(payload) {
      return new Promise((resolve) => {
        this._providerChangeResolve = resolve;
        this.providerChangeDialog = {
          open: true,
          message: payload.message || '',
          effectiveDateDisplay: payload.effectiveDateDisplay || '',
          customerServiceNotice: payload.customerServiceNotice || '',
        };
      });
    },
    confirmProviderChangeDialog() {
      if (this._providerChangeResolve) this._providerChangeResolve(true);
      this._providerChangeResolve = null;
      this.providerChangeDialog.open = false;
    },
    cancelProviderChangeDialog() {
      if (this._providerChangeResolve) this._providerChangeResolve(false);
      this._providerChangeResolve = null;
      this.providerChangeDialog.open = false;
    },
    bumpEnrollmentReload() {
      this.$nextTick(() => {
        this.reloadKey += 1;
      });
    },
    async onSpecialistSelected(provider) {
      if (!this.selectedMember || !this.selectedMember.MEMBER_ID) {
        this.showSnackbar('Error: Member information is missing', 'error');
        return;
      }
      if (!provider || !provider.PROVIDER_ID) {
        this.showSnackbar('Error: Provider information is missing', 'error');
        return;
      }

      const memberId = this.selectedMember.MEMBER_ID;
      const specialty = this.selectedSpecialty;

      try {
        const url = `/api/enrollment/member/${this.insurance}/${memberId}/specialist`;
        const bodyBase = {
          specialty,
          providerId: provider.PROVIDER_ID,
          practiceId: provider.PRACTICE_ID,
        };

        let res = await axios.put(url, bodyBase);
        if (res.data && res.data.requiresConfirmation) {
          const ok = await this.openProviderChangeDialog(res.data);
          if (!ok) {
            this.closeSpecialistModal();
            return;
          }
          res = await axios.put(url, { ...bodyBase, confirmEffectiveNextMonth: true });
        }

        this.closeSpecialistModal();
        const d = res.data;
        const msg =
          d && d.scheduled && d.effectiveDateDisplay
            ? d.message || `Specialist change scheduled for ${d.effectiveDateDisplay}.`
            : 'Specialist updated successfully';
        this.showSnackbar(msg, 'success');
        this.bumpEnrollmentReload();
      } catch (error) {
        const errMsg = error.response && error.response.data && error.response.data.error;
        this.showSnackbar(errMsg ? ('Error: ' + errMsg) : 'Error updating specialist. Please try again.', 'error');
      }
    },
    async onPCPSelected(provider) {
      if (!this.selectedMember || !this.selectedMember.MEMBER_ID) {
        this.showSnackbar('Error: Member information is missing', 'error');
        return;
      }
      if (!provider || !provider.PROVIDER_ID) {
        this.showSnackbar('Error: Provider information is missing', 'error');
        return;
      }

      try {
        const url = `/api/enrollment/member/${this.insurance}/${this.selectedMember.MEMBER_ID}/pcp`;
        const bodyBase = {
          providerId: provider.PROVIDER_ID,
          practiceId: provider.PRACTICE_ID,
        };

        let res = await axios.put(url, bodyBase);
        if (res.data && res.data.requiresConfirmation) {
          const ok = await this.openProviderChangeDialog(res.data);
          if (!ok) {
            this.closePCPModal();
            return;
          }
          res = await axios.put(url, { ...bodyBase, confirmEffectiveNextMonth: true });
        }

        this.closePCPModal();
        const d = res.data;
        const msg =
          d && d.scheduled && d.effectiveDateDisplay
            ? d.message || `PCP change scheduled for ${d.effectiveDateDisplay}.`
            : 'PCP updated successfully';
        this.showSnackbar(msg, 'success');
        this.bumpEnrollmentReload();
      } catch (error) {
        const errMsg = error.response && error.response.data && error.response.data.error;
        this.showSnackbar(errMsg ? ('Error: ' + errMsg) : 'Error updating PCP. Please try again.', 'error');
      }
    },
    showSnackbar(message, color = 'success') {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
  },
};
</script>

<style scoped>
.member-enrollment-panels {
  background: transparent;
}

.member-enrollment-panels .v-expansion-panel {
  background: white;
  margin-bottom: 16px;
  border-radius: 8px !important;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
  transition: box-shadow 0.15s ease;
}

.member-enrollment-panels .v-expansion-panel:hover {
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.12) !important;
}

.member-enrollment-panels .v-expansion-panel--active {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2) !important;
}

.expansion-header {
  padding: 20px 24px !important;
}

.subscriber-card {
  border-left: 4px solid #1976d2;
  transition: box-shadow 0.15s ease;
}

.subscriber-card:hover {
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.15) !important;
}

.dependent-card {
  border-left: 4px solid #424242;
  transition: box-shadow 0.15s ease;
}

.dependent-card:hover {
  box-shadow: 0 4px 12px rgba(66, 66, 66, 0.15) !important;
}

.v-card-title {
  padding: 16px 20px !important;
}

.v-expansion-panel-content >>> .v-expansion-panel-content__wrap {
  padding: 0 !important;
}
</style>
